import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TranslateService } from "@ngx-translate/core";
import { ObjectUtils } from "@bfx/primeng/utils";
import { BfxInputControlStateEnum } from "../bfx-interface-controls/models/bfx-interface-controls.enums";
import { OENEntryType } from "../order-entry/data/enums/oe-entry-type.enum";
import { ICIAutocomplete } from "../order-entry/interface/controls/interface/ic-autocomplete.interface";
import { ICNInputControlState } from "../order-entry/interface/controls/models/bfx-interface-controls.enums";
import { ICAutocompleteGenericModel } from "../order-entry/interface/controls/models/bfx-interface-controls.models";
import { BfxAutoCompleteComponent } from "./bfx-autocomplete.component";

export const ALGO_TYPE_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ICAlgoTypeDefaultsComponent),
  multi: true,
};
/**
 * ICAlgoTypeDefaultsComponent
 * ----------------------------------
 *
 *  This is a copy of the component in ICAlgoTypeDefaultsComponent.
 */
@Component({
  selector: "ic-algo-type-defaults",
  templateUrl: "./html/ic-algo-type-defaults.html",
  providers: [ALGO_TYPE_VALUE_ACCESSOR],
  standalone: false,
})
export class ICAlgoTypeDefaultsComponent
  implements OnInit, OnDestroy, OnChanges, ControlValueAccessor, ICIAutocomplete
{
  /**********************************************************************/
  /* DATA */

  @Input() label: any = "Type";

  @Input() entryType: OENEntryType = OENEntryType.SPOT;

  @Input() dataset: Array<ICAutocompleteGenericModel>;

  @Input() disabled: boolean = false;

  @Input() allowEmpty: boolean = false;

  @Input() isLoading: boolean = false;

  public inputFocused: boolean = false;

  public locked: boolean = false;

  /* this flag is used for immediately checking */
  input_validationState: BfxInputControlStateEnum;
  @Output()
  validationStateChange: EventEmitter<BfxInputControlStateEnum | null> =
    new EventEmitter<BfxInputControlStateEnum | null>();
  @Input()
  get validationState() {
    return this.input_validationState;
  }

  set validationState(value: any) {
    this.input_validationState = value;
    this.validationStateChange.emit(this.input_validationState);
  }

  public data: any = {
    // selection
    item: null, // used in the autocomplete interface for display (intermediate model)
    value: null, // item selected in the autocomplete in native (Impl model)
    dataset: [], // complete dataset of all autocomplete options, used for local filtering
    filteredItems: [], //
    state: ICNInputControlState.INACTIVE, // validity state of the selected/inputted item
  };

  onModelChange: Function = (value: any) => {};
  onModelTouched: Function = () => {};

  @ViewChild("inputField") inputField: BfxAutoCompleteComponent;

  /**********************************************************************/
  /* External Validation */
  input_externalState: ICNInputControlState;

  @Input()
  get externalState() {
    return this.input_externalState;
  }

  set externalState(value: any) {
    this.input_externalState = value;
  }

  @Output("onBlur") blur: EventEmitter<any> = new EventEmitter<any>();

  @Output("onSelect") itemSelect: EventEmitter<any> = new EventEmitter<any>();

  @Output("onFocus") focus: EventEmitter<any> = new EventEmitter<any>();

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */

  constructor(
    public element: ElementRef,
    public objectUtils: ObjectUtils,
    public translationService: TranslateService,
    public cd: ChangeDetectorRef,
  ) {}

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    for (let propName in changes) {
      if (propName === "dataset") {
        if (this.dataset instanceof Array) {
          this.setDataset(this.dataset);
        } else {
          this.data.dataset = [];
        }
      } else if (propName === "disabled") {
        if (this.disabled != null || typeof this.disabled !== "boolean") {
          this.disabled = false;
        }
      } else if (propName === "externalState") {
        if (this.input_externalState != null) {
          this.data.state = this.input_externalState;
          this.validationState = this.input_externalState;
        }
      }
      // IGNORED
      /* else if(propName === 'entryType' || propName === 'label' || propName === 'allowEmpty' || propName === 'isLoading'){
					  // do nothing;
				  } */
    }
  }

  ngOnDestroy() {}

  /**********************************************************************/
  /* ControlValueAccessor */

  writeValue(value: any): void {
    try {
      this.data.value = value;
    } catch (ex) {
      logger.out.error("EXCEPTION: icAlgoType.writeValue(): %O", ex);
    }
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  /**********************************************************************/
  /* EVENTS */
  public onFocus($event: any): void {
    this.inputFocused = true;
    this.data.state = ICNInputControlState.ACTIVE;
    this.validationState = ICNInputControlState.ACTIVE;
    this.focus.emit(true);
  }

  public onBlur($event: any): void {
    this.inputFocused = false;

    this.cd.markForCheck();

    if (this.validate()) {
      this.blur.emit(this.data.value);
    }

    this.updateValue(this.data.value);
  }

  // for propagation
  public onSelect($event: any): void {
    this.cd.markForCheck();

    if (this.data.item == null || this.validate()) {
      this.updateValue(this.data.value);
      this.itemSelect.emit(
        this.data.item instanceof ICAutocompleteGenericModel
          ? this.data.item.value
          : this.data.item,
      );
    }
  }

  /**********************************************************************/
  /* EXTERNAL API */
  public updateValue(value: any): void {
    this.writeValue(value);
    this.onModelChange(this.data.value);
    this.blur.emit(this.data.value);
  }

  public reset(): void {
    this.data.item = "";
    this.data.value = null;
    this.data.dataset = this.dataset;
    this.data.filteredItems = [];
    this.externalState = null;
    this.data.state = ICNInputControlState.INACTIVE;
    this.validationState = ICNInputControlState.INACTIVE;
    this.updateValue(this.data.value);
  }

  public load(value: ICAutocompleteGenericModel, propagate: boolean = false) {
    if (value instanceof ICAutocompleteGenericModel || value === null) {
      this.set(value);
      this.validate();
      if (propagate) {
        this.updateValue(this.data.value);
      }
    }
  }

  public set(value: ICAutocompleteGenericModel): void {
    if (value instanceof ICAutocompleteGenericModel || value === null) {
      this.data.value = value;
      this.data.item = value;
    }
  }

  public setDataset(dataset: Array<ICAutocompleteGenericModel>): void {
    this.data.dataset = dataset;
  }

  public lock(): void {
    this.locked = true;
  }

  public unlock(): void {
    this.locked = false;
  }

  public hidePanel() {
    this.inputFocused = false;
    this.inputField.hide();
  }

  public validate(): boolean {
    let selectedItem = this.data.item;
    if (selectedItem != null && selectedItem !== "") {
      let result = this.data.dataset.find((item) => {
        if (selectedItem.label != null) {
          return item.label.toLowerCase() === selectedItem.label.toLowerCase();
        }
        return item.label.toLowerCase() === selectedItem.toLowerCase();
      });
      if (result != null) {
        this.data.state = ICNInputControlState.VALID;
        this.validationState = ICNInputControlState.VALID;
        this.externalState = null;
        this.set(result);
        return true;
      } else if (this.data.state !== ICNInputControlState.INACTIVE) {
        this.data.state = ICNInputControlState.INVALID;
        this.validationState = ICNInputControlState.INVALID;
        //	this.set(null);
        return false;
      } else {
        this.data.state = ICNInputControlState.INACTIVE;
        this.validationState = ICNInputControlState.INACTIVE;
        //	this.set(null);
        return false;
      }
    } else {
      if (this.data.state !== ICNInputControlState.INACTIVE && !this.allowEmpty) {
        this.data.state = ICNInputControlState.INVALID;
        this.validationState = ICNInputControlState.INVALID;
        //	this.set(null);
        return false;
      } else if (this.data.state !== ICNInputControlState.INACTIVE && this.allowEmpty) {
        this.data.state = ICNInputControlState.INACTIVE;
        this.validationState = ICNInputControlState.INACTIVE;
        this.set(null);
        return true;
      }
    }
  }

  /**********************************************************************/
  /* INTERNAL API */

  public search(event: any): void {
    let filteredItems = [];
    let allItems = this.data.dataset;

    for (let i = 0; i < allItems.length; i++) {
      let item = allItems[i];
      if (
        event.query.length === 0 ||
        item.label.toLowerCase().indexOf(event.query.toLowerCase()) >= 0
      ) {
        filteredItems.push(item);
      }
    }
    this.data.filteredItems = filteredItems;
  }
}
